import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const SECTIONS = [
  {
    title: 'Information we collect',
    content: (
      <>
        <p>We collect information needed to provide and operate Skill Share, including:</p>
        <ul>
          <li>Account details such as name, email address, mobile number, password credentials, and profile information.</li>
          <li>College or organization details when access is provided through an institutional portal.</li>
          <li>Learning activity such as enrolled courses, watched lessons, completion status, assessments, certificates, attendance, and progress.</li>
          <li>Career information that you choose to provide, including resumes, skills, job applications, enquiries, projects, and internship interests.</li>
          <li>Payment and subscription records. Payment-card or banking information may be processed directly by the selected payment provider.</li>
          <li>Technical information such as device, browser, IP address, login activity, diagnostics, and notification preferences.</li>
        </ul>
      </>
    ),
  },
  {
    title: 'How we use information',
    content: (
      <ul>
        <li>To create and secure accounts, authenticate users, and provide authorized access.</li>
        <li>To deliver courses, live classes, assessments, certificates, jobs, internships, and related services.</li>
        <li>To record learning progress and provide relevant recommendations and reminders.</li>
        <li>To process enquiries, enrollments, subscriptions, payments, and support requests.</li>
        <li>To communicate service updates, learning notifications, and important account information.</li>
        <li>To maintain security, prevent misuse, troubleshoot problems, and improve the platform.</li>
        <li>To comply with applicable legal, regulatory, accounting, and contractual requirements.</li>
      </ul>
    ),
  },
  {
    title: 'College and organization access',
    content: (
      <p>
        If you register through a college or organization portal, relevant registration, authorization, enrollment,
        attendance, and learning-progress information may be available to authorized representatives of that institution.
        Each institution is responsible for using that information only for legitimate educational and administrative purposes.
      </p>
    ),
  },
  {
    title: 'How information is shared',
    content: (
      <>
        <p>We do not sell personal information. We may share limited information with:</p>
        <ul>
          <li>Authorized administrators, faculty, mentors, team leads, colleges, or organizations supporting your learning.</li>
          <li>Service providers that support hosting, communications, notifications, analytics, payments, and platform operations.</li>
          <li>Employers or opportunity providers when you intentionally apply, enquire, or submit information for an opportunity.</li>
          <li>Government, regulatory, or law-enforcement authorities when disclosure is legally required.</li>
          <li>A successor organization if the platform or relevant business assets are reorganized or transferred, subject to applicable law.</li>
        </ul>
      </>
    ),
  },
  {
    title: 'Cookies and local storage',
    content: (
      <p>
        Skill Share may use cookies, browser storage, and similar technologies to keep you signed in, remember preferences,
        protect sessions, measure service performance, and provide core platform functionality. Disabling essential storage
        may prevent parts of the application from working correctly.
      </p>
    ),
  },
  {
    title: 'Data retention and security',
    content: (
      <p>
        We retain information for as long as needed to provide services, maintain learning and payment records, meet legal
        obligations, resolve disputes, and protect the platform. We use reasonable administrative, technical, and organizational
        safeguards, but no internet-based service can guarantee absolute security.
      </p>
    ),
  },
  {
    title: 'Your choices and rights',
    content: (
      <p>
        Subject to applicable law and institutional requirements, you may request access to, correction of, or deletion of
        personal information, or withdraw consent where processing depends on consent. Some records may need to be retained for
        educational, transaction, security, or legal purposes. You can also manage supported notification permissions through
        your browser, device, or application settings.
      </p>
    ),
  },
  {
    title: 'Children and student accounts',
    content: (
      <p>
        Student accounts should be created with appropriate institutional or parent/guardian authorization where required.
        Colleges and organizations must ensure that they have the necessary authority and notices in place before providing
        student information or enabling student access.
      </p>
    ),
  },
  {
    title: 'Third-party services',
    content: (
      <p>
        The platform may link to payment providers, video services, employers, learning resources, or other third-party services.
        Their privacy practices are governed by their own policies, and users should review those policies before providing information.
      </p>
    ),
  },
  {
    title: 'Policy updates and contact',
    content: (
      <p>
        We may update this policy when the platform, legal requirements, or data practices change. The latest version will remain
        available at this URL with its effective date. For privacy questions or requests, contact Top Skilled Academy through its
        official support or contact channels and include the email address associated with your Skill Share account.
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Privacy Policy | Skill Share';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="privacy-policy-page">
      <header className="privacy-policy-header">
        <Link to="/login" className="privacy-policy-brand" aria-label="Skill Share login">
          <img src="/favicon.png" alt="" />
          <span><strong>Skill Share</strong><small>by Top Skilled Academy</small></span>
        </Link>
        <Link to="/login" className="privacy-policy-login"><i className="bi bi-arrow-left" /> Back to Login</Link>
      </header>

      <article className="privacy-policy-document">
        <div className="privacy-policy-intro">
          <span><i className="bi bi-shield-check" /> Your privacy matters</span>
          <h1>Privacy Policy</h1>
          <p>
            This Privacy Policy explains how Top Skilled Academy collects, uses, shares, and protects information when you use
            Skill Share through the web application, mobile application, college portals, and related learning services.
          </p>
          <div>Effective date: 25 September 2026</div>
        </div>

        <div className="privacy-policy-sections">
          {SECTIONS.map((section, index) => (
            <section key={section.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><h2>{section.title}</h2>{section.content}</div>
            </section>
          ))}
        </div>
      </article>

      <footer className="privacy-policy-footer">
        <span>Skill Share by Top Skilled Academy</span>
        <span>Privacy Policy</span>
      </footer>
    </main>
  );
}
